import type { Subscription } from '@/features/billing/api/billing';

/**
 * Whether a merchant has actually settled a plan.
 *
 * The question sign-up asks: is there money arranged, so this account can stop
 * being steered back to the payment screen? `useWorkspace` asks it to choose a
 * landing page and the subscribe screen asks it to choose where a finished
 * checkout hands on, and the two must agree - a header that links somewhere its
 * destination would bounce is the bug this shape exists to rule out.
 *
 * Deliberately *not* the question the billing page asks. `BillingPage` keeps a
 * wider set of its own that also counts `PAUSED`, because it is answering "is
 * this mandate still the thing charging them" - a paused mandate is, and a
 * merchant holding one still has a plan to manage. Folding the two together
 * would quietly change who gets sent back to checkout.
 */
export const SETTLED_STATES = new Set(['TRIALING', 'ACTIVE', 'PAST_DUE', 'CANCEL_SCHEDULED']);

export function isSettled(summary: Subscription, now: Date = new Date()): boolean {
  // The free dashboard trial: open while it runs, whatever else the summary
  // says, and closed the moment it has run out with no plan - even if an
  // abandoned checkout left an "active" row with no plan on it behind.
  const trial = summary.panel_trial ?? null;
  if (trial?.expired) return false;
  if (panelTrialRunning(summary, now)) return true;
  const mandate = summary.recurring ?? null;
  // A mandate Razorpay is already honouring counts even where the period flag
  // has not caught up with it: the merchant has authorised the money, and
  // asking them for it twice is the one outcome this must never produce.
  if (mandate !== null && SETTLED_STATES.has(mandate.state)) return true;
  return summary.active === true;
}

/** Whether the free dashboard trial is running, by this browser's clock. */
export function panelTrialRunning(summary: Subscription, now: Date = new Date()): boolean {
  const trial = summary.panel_trial ?? null;
  if (!trial?.active || !trial.ends_at) return false;
  return new Date(trial.ends_at).getTime() > now.getTime();
}

/**
 * Days left in the free trial, counting a part-day as a day: a 5-day trial
 * reads 5 the moment it starts, and 1 throughout its last 24 hours (the
 * badge's "last day"). Null when no trial is running.
 */
export function panelTrialDaysLeft(summary: Subscription, now: Date = new Date()): number | null {
  if (!panelTrialRunning(summary, now)) return null;
  const ends = new Date(summary.panel_trial?.ends_at ?? 0).getTime();
  return Math.max(1, Math.ceil((ends - now.getTime()) / 86_400_000));
}
